import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as NeedleManagementActions from './needle-management.actions';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { NeedleManagementFacade } from './needle-management.facade';
import { TNeedleManagementInfoActionType } from './needle-management.models';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class NeedleManagementEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private needleManagementFacade: NeedleManagementFacade,
    private APIHelper: AppNotificationUtilClass,
    private router: Router,
  ) {}

  getNeedleList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.GetNeedleList),
      switchMap((action: NeedleManagementActions.GetNeedleListInfo) =>
        this.needleManagementFacade.getNeedleListPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService
          .fetchNeedleList({
            ...action.payload,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new NeedleManagementActions.GetNeedleListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new NeedleManagementActions.GetNeedleListInfoFailure());
            }),
          ),
      ),
    ),
  );

  updateNeedle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.UpdateNeedle),
      switchMap((action: NeedleManagementActions.UpdateNeedle) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.updateNeedle(action.updatedNeedle).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.needleManagementFacade.resetNeedleListInfo('ACTION');
            return new NeedleManagementActions.UpdateNeedleSuccess();
          }),
          catchError(() => {
            return of(new NeedleManagementActions.UpdateNeedleFailure());
          }),
        ),
      ),
    ),
  );

  deleteNeedle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.DeleteNeedle),
      switchMap((action: NeedleManagementActions.DeleteNeedle) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService
          .deleteNeedle({
            needleIds: action.needleIds,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map(() => {
              this.needleManagementFacade.resetNeedleListInfo('ACTION');
              return new NeedleManagementActions.DeleteNeedleSuccess();
            }),
            catchError(() => {
              return of(new NeedleManagementActions.DeleteNeedleFailure());
            }),
          ),
      ),
    ),
  );

  getNeedleDropdownInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.GetNeedleListDropdown),
      switchMap((action: NeedleManagementActions.GetNeedleListDropdown) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getNeedleListDropdownData().pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new NeedleManagementActions.GetNeedleListDropdownSuccess(res);
          }),
          catchError(() => {
            return of(new NeedleManagementActions.GetNeedleListDropdownFailure());
          }),
        ),
      ),
    ),
  );

  raiseNeedleRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.RaiseNeedleRequest),
      switchMap((action: NeedleManagementActions.RaiseNeedleRequest) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.raiseNeedleRequest(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.needleManagementFacade.resetNeedleListInfo('ACTION');
            return new NeedleManagementActions.RaiseNeedleRequestSuccess();
          }),
          catchError(() => {
            return of(new NeedleManagementActions.RaiseNeedleRequestFailure());
          }),
        ),
      ),
    ),
  );

  getNeedleRequestList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.GetNeedleRequestList),
      switchMap((action: NeedleManagementActions.GetNeedleRequestList) =>
        this.needleManagementFacade.getNeedleRequestPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService
          .getNeedleRequestList({
            ...action.payload,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new NeedleManagementActions.GetNeedleRequestListSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new NeedleManagementActions.GetNeedleRequestListFailure());
            }),
          ),
      ),
    ),
  );

  bulkUpdateNeedleRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.BulkUpdateNeedleRequest),
      switchMap((action: NeedleManagementActions.BulkUpdateNeedleRequest) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateNeedleRequest(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/approvals/needle-request'], {
              queryParams: { requestStatus: action.payload.updatedStatus },
            });
            return new NeedleManagementActions.BulkUpdateNeedleRequestSuccess();
          }),
          catchError(() => {
            return of(new NeedleManagementActions.BulkUpdateNeedleRequestFailure());
          }),
        ),
      ),
    ),
  );

  approveNeedleRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.ApproveNeedleRequest),
      switchMap((action: NeedleManagementActions.ApproveNeedleRequest) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.approveNeedleRequest(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.needleManagementFacade.resetNeedleRequestList('ACTION', action.status);
            return new NeedleManagementActions.ApproveNeedleRequestSuccess();
          }),
          catchError(() => {
            return of(new NeedleManagementActions.ApproveNeedleRequestFailure());
          }),
        ),
      ),
    ),
  );

  getLocalStoreRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TNeedleManagementInfoActionType.GetLocalStoreRequests),
      switchMap((action: NeedleManagementActions.GetLocalStoreRequests) =>
        this.needleManagementFacade.getLocalNeedleRequestPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService
          .getLocalStoreRequests({
            ...action.payload,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new NeedleManagementActions.GetLocalStoreRequestsSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new NeedleManagementActions.GetLocalStoreRequestsFailure());
            }),
          ),
      ),
    ),
  );
}
